<template>
    <div v-if="onGrid">

        <div class="w-full border-t-2 border-blue-600">
            <div class="border bg-tan-200 flex flex-wrap">
                <div class="mt-2 pl-2 inline-block">
                    <div class="mr-2 inline-flex">
                        <settings
                            @apply-settings="applySettings"
                            :initial-settings="gSettings">
                        </settings>
                    </div>
                    <div class="mr-2 inline-flex">
                        <indicators
                            :chart-instance="gChart"
                            @remove="removeIndicator"
                            @apply-indicators="addIndicator">
                        </indicators>
                    </div>
                </div>
                <div class="mt-2 px-2 inline">
                    <grid-chart-type-dropdown
                        :initial-type="gridType"
                        @changed="changeGridType">
                    </grid-chart-type-dropdown>
                </div>
                <div class="sm:ml-auto sm:pr-2">
                    <templates
                        @apply="handleApplyTemplate"
                        :display-save-link="false"
                        :default-template="userDefaultTemplate"
                        :initial-default="userDefaultTemplate || null"
                        :initial-templates="props.templates"
                        :initial-public-templates="props.publicTemplates">
                    </templates>
                </div>
            </div>
        </div>

        <div id="chart-grid-mode" class="h-64">
            <div class="h-full mt-4" v-if="gridType === '2x2'">
                <div class="flex w-full" style="height: 50%">
                    <grid-chart :index="0" :section="props.section" @new-chart="updateChart" :symbol="props.symbol" :default-template="defaultTemplate" class="w-1/2"></grid-chart>
                    <grid-chart :index="1" :section="props.section" :symbol="props.symbol" :default-template="defaultTemplate" class="w-1/2"></grid-chart>
                </div>
                <div class="flex w-full" style="height: 50%">
                    <grid-chart :index="2" :section="props.section" :symbol="props.symbol" :default-template="defaultTemplate" class="w-1/2"></grid-chart>
                    <grid-chart :index="3" :section="props.section" :symbol="props.symbol" :default-template="defaultTemplate" class="w-1/2"></grid-chart>
                </div>
            </div>

            <div class="h-full mt-4" v-if="gridType === '1x2'">
                <div class="flex w-full" style="height: 50%">
                    <grid-chart :index="0" :section="props.section" @new-chart="updateChart" :symbol="props.symbol" :default-template="defaultTemplate" class="w-full"></grid-chart>
                </div>
                <div class="flex w-full" style="height: 50%">
                    <grid-chart :index="1" :section="props.section" :symbol="props.symbol" :default-template="defaultTemplate" class="w-full"></grid-chart>
                </div>
            </div>

            <div class="h-full mt-4" v-if="gridType === '1x3'">
                <div class="flex w-full" style="height: 33%">
                    <grid-chart :index="0" :section="props.section" @new-chart="updateChart" :symbol="props.symbol" :default-template="defaultTemplate" class="w-full"></grid-chart>
                </div>
                <div class="flex w-full" style="height: 33%">
                    <grid-chart :index="1" :section="props.section" :symbol="props.symbol" :default-template="defaultTemplate" class="w-full"></grid-chart>
                </div>
                <div class="flex w-full" style="height: 33%">
                    <grid-chart :index="2" :section="props.section" :symbol="props.symbol" :default-template="defaultTemplate" class="w-full"></grid-chart>
                </div>
            </div>

            <div class="h-full mt-4" v-if="gridType === '2x1'">
                <div class="flex h-full w-full">
                    <grid-chart :index="0" :section="props.section" @new-chart="updateChart" :symbol="props.symbol" :default-template="defaultTemplate" class="w-1/2"></grid-chart>
                    <grid-chart :index="1" :section="props.section" :symbol="props.symbol" :default-template="defaultTemplate" class="w-1/2"></grid-chart>
                </div>
            </div>

            <div class="h-full mt-4" v-if="gridType === '3x1'">
                <div class="flex h-full w-full">
                    <grid-chart :index="0" :section="props.section" @new-chart="updateChart" :symbol="props.symbol" :default-template="defaultTemplate" class="w-1/3"></grid-chart>
                    <grid-chart :index="1" :section="props.section" :symbol="props.symbol" :default-template="defaultTemplate" class="w-1/3"></grid-chart>
                    <grid-chart :index="1" :section="props.section" :symbol="props.symbol" :default-template="defaultTemplate" class="w-1/3"></grid-chart>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, inject } from 'vue';

import Templates from './Templates';
import Indicators from './Indicators';
import Settings from './Settings';
import GridChart from './GridChart';
import GridChartTypeDropdown from './GridChartTypeDropdown';

import { useChartDefaultValues } from './mixins/ChartDefaultValues';

const props = defineProps({
    userDefaultTemplate: Object,
    templates: Array,
    publicTemplates: Array,
    defaultFeedMode: String,
    section: String,
    symbol: {
        type: String,
        required: true
    }
});

// global chart iunstance
// let gChart = null;
const gChart = ref(null);

const eventBus = inject('eventBus');
const chartDefaultValues = useChartDefaultValues();

const gSettings = ref({});
const defaultTemplate = ref(props.userDefaultTemplate);
const onGrid = ref(false);
const gridType = ref(getInitialType() || '2x1');

const changeGridType = (newType) => {
    onGrid.value = false;
    nextTick(() => {
        onGrid.value = true;
        gridType.value = newType;
        if (newType === null) {
            return turnGridOff();
        } else {
            setInitialType(newType);
        }
    });
};

/**
* Used to keep indicators in sync
* Emitted from child grid-chart instances
*/
function updateChart(newChartInstance) {
    // gChart = newChartInstance;
    gChart.value = newChartInstance;
}

/**
* Apply new settings to all child charts
*/
function applySettings(newSettings) {
    eventBus.emit('grid-settings-updated', newSettings);
}

/**
* Apply template to all child charts
*/
function handleApplyTemplate(pTemplate) {
    eventBus.emit('grid-apply-template', JSON.stringify(pTemplate.template));
    gSettings.value = chartDefaultValues.getInitialValues(pTemplate.template, false);
}

function removeIndicator(pIndex) {
    const action = {
        id: 'Plot',
        context: {
            id: 'Delete',
            type: 'Study',
            index: pIndex
        }
    };

    eventBus.emit('grid-indicator-changed', action);
}

function addIndicator(indicator) {
    eventBus.emit('grid-indicator-changed', indicator);
}

function getInitialType() {
    const pType = localStorage.getItem('default-grid-view-type');

    // For backwards compatability
    if (pType === 'null') {
        return;
    }

    return pType;
}

function setInitialType(pType) {
    return localStorage.setItem('default-grid-view-type', pType);
}

function turnGridOff() {
    eventBus.emit('grid-mode-toggled', false);
    onGrid.value = false;
}

function turnGridOn() {
    eventBus.emit('grid-mode-toggled', true);

    onGrid.value = true;

    // If grid mode was turned off then back on
    if (gridType.value === null) {
        gridType.value = getInitialType() || '2x1';
    }
}

onMounted(() => {
    const initialSettings = defaultTemplate.value && defaultTemplate.value.template ? defaultTemplate.value.template : {};
    gSettings.value = chartDefaultValues.getInitialValues(initialSettings);
    eventBus.on('grid-mode-on', turnGridOn);
});

onUnmounted(() => {
    eventBus.off('grid-mode-on', turnGridOn);
});
</script>

<style lang="scss">
    #chart-grid-mode {
        height: 800px;
    }

    #chart-1, #chart-2, #chart-3, #chart-4 {
        @apply bg-blue-500 m-4;
    }
</style>
